(() => {
  if (!window.Zeta) {
    return;
  }

  const FACET_DEFS = [
    { key: 'style', label: 'スタイル', hasChild: false, useName: false },
    { key: 'category', label: 'カテゴリー', hasChild: true, useName: false },
    { key: 'size', label: 'サイズ', hasChild: false, useName: false },
    { key: 'color', label: 'カラー', hasChild: false, useName: true },
  ];

  const FILTER_KEYS = ['style', 'category', 'size', 'color', 'price'];
  const PRICE_SLIDER_MIN = 0;
  const PRICE_SLIDER_MAX_FALLBACK = 130000;
  const PRICE_SLIDER_STEP = 100;
  const DEFAULT_SORT = 'sales_start_at';
  const SEARCH_HISTORY_KEY = 'zeta_search_history';
  const SEARCH_HISTORY_MAX = 10;
  const DRAWER_TRANSITION_MS = 300;

  class ZetaSearchPage extends HTMLElement {
    connectedCallback() {
      this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || window.Zeta.config.perPage;
      this.priceSliderMaximum = PRICE_SLIDER_MAX_FALLBACK;

      this.refs = {
        title: this.querySelector('[data-zeta-title]'),
        searchForm: this.querySelector('[data-zeta-search-form]'),
        searchInput: this.querySelector('[data-zeta-search-input]'),
        count: this.querySelector('[data-zeta-count]'),
        sort: this.querySelector('[data-zeta-sort]'),
        filterToggle: this.querySelector('[data-zeta-filter-toggle]'),
        drawer: this.querySelector('[data-zeta-drawer]'),
        drawerOverlay: this.querySelector('[data-zeta-drawer-overlay]'),
        clear: this.querySelector('[data-zeta-clear]'),
        apply: this.querySelector('[data-zeta-apply]'),
        facets: this.querySelector('[data-zeta-facets]'),
        active: this.querySelector('[data-zeta-active]'),
        grid: this.querySelector('[data-zeta-grid]'),
        loading: this.querySelector('[data-zeta-loading]'),
        error: this.querySelector('[data-zeta-error]'),
        empty: this.querySelector('[data-zeta-empty]'),
        emptyPopular: this.querySelector('[data-zeta-empty-popular]'),
        pagination: this.querySelector('[data-zeta-pagination]'),
        cardTemplate: this.querySelector('[data-zeta-card-template]'),
        facetGroupTemplate: this.querySelector('[data-zeta-facet-group-template]'),
        facetOptionTemplate: this.querySelector('[data-zeta-facet-option-template]'),
      };

      this.state = this.readStateFromUrl();
      this.draft = this.cloneFilterState(this.state);
      this.optionLabels = {};
      this.saveSearchHistory(this.state.q);
      this.renderTitle();
      this.bindEvents();
      // 上限取得と初回検索は並列に投げる。上限は後から確定してよい（スライダーはドロワー内で初期状態は非表示）。
      this.loadPriceSliderMaximum();
      this.search();
    }

    readStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const filters = {};
      FILTER_KEYS.forEach((key) => {
        const value = params.get(key);
        filters[key] = value ? value.split(',').filter(Boolean) : [];
      });
      return {
        q: params.get('q') || '',
        sort: params.get('sort') || DEFAULT_SORT,
        page: Math.max(1, parseInt(params.get('page'), 10) || 1),
        instock: params.get('instock') === '1',
        filters: filters,
      };
    }

    writeStateToUrl() {
      const params = new URLSearchParams();
      params.set('q', this.state.q);
      params.set('view', 'zeta');
      if (this.state.sort && this.state.sort !== DEFAULT_SORT) {
        params.set('sort', this.state.sort);
      }
      if (this.state.page > 1) {
        params.set('page', String(this.state.page));
      }
      if (this.state.instock) {
        params.set('instock', '1');
      }
      FILTER_KEYS.forEach((key) => {
        if (this.state.filters[key] && this.state.filters[key].length > 0) {
          params.set(key, this.state.filters[key].join(','));
        }
      });
      window.history.pushState(this.state, '', `${window.location.pathname}?${params.toString()}`);
    }

    bindEvents() {
      if (this.refs.searchForm && this.refs.searchInput) {
        this.refs.searchForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.state.q = this.refs.searchInput.value.trim();
          this.state.page = 1;
          this.saveSearchHistory(this.state.q);
          this.writeStateToUrl();
          this.renderTitle();
          this.search();
        });
      }

      if (this.refs.sort) {
        this.refs.sort.value = this.state.sort;
        this.refs.sort.addEventListener('change', () => {
          this.state.sort = this.refs.sort.value;
          this.state.page = 1;
          this.writeStateToUrl();
          this.search();
        });
      }

      if (this.refs.filterToggle) {
        this.refs.filterToggle.addEventListener('click', () => this.openDrawer());
        this.refs.drawerOverlay.addEventListener('click', () => this.closeDrawer());
        this.refs.clear.addEventListener('click', () => this.clearDraft());
        this.refs.apply.addEventListener('click', () => this.applyDraft());
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.refs.drawer.getAttribute('aria-hidden') === 'false') {
            this.closeDrawer();
          }
        });
      }

      window.addEventListener('popstate', () => {
        this.state = this.readStateFromUrl();
        this.draft = this.cloneFilterState(this.state);
        if (this.refs.sort) {
          this.refs.sort.value = this.state.sort;
        }
        this.search();
      });
    }

    async search() {
      this.setState('loading');
      try {
        const result = await window.Zeta.fetchItems({
          q: this.state.q,
          l: this.perPage,
          o: (this.state.page - 1) * this.perPage,
          s: this.state.sort,
          stock: this.state.instock ? 1 : 0,
          filters: { ...this.state.filters },
        });

        this.renderTitle();
        this.renderCount(result.total);
        this.renderGrid(result.items);
        this.renderFacets(result.facets);
        this.renderActiveFilters();
        this.renderPagination(result.total);
        const mode = result.items.length === 0 ? 'empty' : 'results';
        this.setState(mode);
        if (mode === 'empty') {
          await this.renderEmptyPopularWords();
        }
        if (mode === 'results') {
          await window.Zeta.hydrateProductCards(this.refs.grid, {
            cardRootSelector: 'product-item',
            linkSelectors: ['[data-zeta-link]', '[data-zeta-title-link]'],
            soldSelector: '[data-zeta-sold]',
          });
          this.applyResolvedSoldOutOrder();
        }
      } catch (error) {
        console.error('[ZETA]', error);
        this.setState('error');
      }
    }

    async loadPriceSliderMaximum() {
      let maximumPrice;
      try {
        maximumPrice = await window.Zeta.fetchMaximumPrice();
      } catch (error) {
        console.error('[ZETA]', error);
        return;
      }
      if (maximumPrice == null || !Number.isFinite(maximumPrice) || maximumPrice <= PRICE_SLIDER_MIN) {
        return;
      }
      const priceSliderMaximum = Math.ceil(maximumPrice / PRICE_SLIDER_STEP) * PRICE_SLIDER_STEP;
      if (priceSliderMaximum === this.priceSliderMaximum) {
        return;
      }
      this.priceSliderMaximum = priceSliderMaximum;
      this.refreshPriceSlider();
    }

    refreshPriceSlider() {
      if (!this.currentFacets) {
        return;
      }
      if (!this.refs.facets || !this.refs.facets.querySelector('.zeta-price-range')) {
        return;
      }
      if (this.refs.facets.contains(document.activeElement)) {
        return;
      }
      this.renderFacets(this.currentFacets);
    }

    cloneFilterState(source) {
      const filters = {};
      FILTER_KEYS.forEach((key) => {
        filters[key] = [...source.filters[key]];
      });
      return { instock: source.instock, filters };
    }

    openDrawer() {
      window.clearTimeout(this.drawerCloseTimer);
      this.draft = this.cloneFilterState(this.state);
      this.renderFacets(this.currentFacets || {});
      this.renderActiveFilters();
      this.refs.drawerOverlay.hidden = false;
      window.requestAnimationFrame(() => this.classList.add('zeta-search--drawer-open'));
      this.refs.drawer.setAttribute('aria-hidden', 'false');
      this.refs.filterToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('zeta-drawer-lock');
    }

    closeDrawer() {
      this.classList.remove('zeta-search--drawer-open');
      this.refs.drawer.setAttribute('aria-hidden', 'true');
      this.refs.filterToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('zeta-drawer-lock');
      this.drawerCloseTimer = window.setTimeout(() => {
        this.refs.drawerOverlay.hidden = true;
      }, DRAWER_TRANSITION_MS);
    }

    clearDraft() {
      FILTER_KEYS.forEach((key) => {
        this.draft.filters[key] = [];
      });
      this.draft.instock = false;
      this.renderFacets(this.currentFacets || {});
      this.renderActiveFilters();
    }

    applyDraft() {
      this.state.filters = this.cloneFilterState(this.draft).filters;
      this.state.instock = this.draft.instock;
      this.state.page = 1;
      this.writeStateToUrl();
      this.closeDrawer();
      this.search();
    }

    setState(mode) {
      this.currentMode = mode;
      this.refs.loading.hidden = mode !== 'loading';
      this.refs.error.hidden = mode !== 'error';
      this.refs.empty.hidden = mode !== 'empty';
      this.refs.grid.hidden = mode !== 'results';
      if (mode !== 'results') {
        this.refs.pagination.hidden = true;
      }
      if (mode !== 'empty' && this.refs.emptyPopular) {
        this.refs.emptyPopular.hidden = true;
      }
    }

    saveSearchHistory(query) {
      const keyword = String(query || '').trim();
      if (!keyword) {
        return;
      }
      try {
        const stored = JSON.parse(window.localStorage.getItem(SEARCH_HISTORY_KEY) || '[]');
        const history = Array.isArray(stored) ? stored : [];
        const nextHistory = [keyword, ...history.filter((entry) => entry !== keyword)].slice(0, SEARCH_HISTORY_MAX);
        window.localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(nextHistory));
      } catch (error) {
        console.error('[ZETA] search history', error);
      }
    }

    async renderEmptyPopularWords() {
      if (!this.refs.emptyPopular) {
        return;
      }
      this.refs.emptyPopular.hidden = true;
      this.refs.emptyPopular.innerHTML = '';
      try {
        const suggest = await window.Zeta.fetchSuggest('');
        if (this.currentMode !== 'empty' || suggest.popularWords.length === 0) {
          return;
        }
        const title = document.createElement('p');
        title.className = 'zeta-search__empty-popular-title';
        title.textContent = '人気のキーワード';
        const chips = document.createElement('div');
        chips.className = 'zeta-search__empty-popular-chips';
        suggest.popularWords.forEach((keyword) => {
          const link = document.createElement('a');
          link.className = 'zeta-search__empty-popular-link';
          link.href = window.Zeta.buildResultsUrl(keyword);
          link.textContent = keyword;
          chips.appendChild(link);
        });
        this.refs.emptyPopular.appendChild(title);
        this.refs.emptyPopular.appendChild(chips);
        this.refs.emptyPopular.hidden = false;
      } catch (error) {
        console.error('[ZETA] empty popular words', error);
      }
    }

    renderTitle() {
      if (this.refs.title) {
        this.refs.title.textContent = this.state.q ? `検索結果：${this.state.q}` : '検索結果';
      }
      if (this.refs.searchInput) {
        this.refs.searchInput.value = this.state.q;
      }
    }

    renderCount(total) {
      if (this.refs.count) {
        this.refs.count.textContent = `全${total.toLocaleString('ja-JP')}商品`;
      }
    }

    // 在庫なしを後ろへ回す。ZETA APIに在庫のソートキーが無いため表示中のページ内で並べ替える。
    renderGrid(items) {
      const inStockItems = items.filter((item) => !item.soldOut);
      const soldOutItems = items.filter((item) => item.soldOut);
      const orderedItems = inStockItems.concat(soldOutItems);

      this.refs.grid.innerHTML = '';
      const fragment = document.createDocumentFragment();
      orderedItems.forEach((item) => {
        fragment.appendChild(this.buildCard(item));
      });
      this.refs.grid.appendChild(fragment);
    }

    // DOMを移動するとWishlist Kingのカスタム要素が再接続され二重初期化されるため、CSS order で見た目だけ並べ替える。
    applyResolvedSoldOutOrder() {
      this.refs.grid.querySelectorAll('product-item').forEach((card) => {
        const wishlistSlot = card.querySelector('[data-zeta-wishlist-slot]');
        card.style.order = wishlistSlot?.dataset.zetaSoldResolved === '1' ? '1' : '0';
      });
    }

    buildCard(item) {
      const node = this.refs.cardTemplate.content.cloneNode(true);
      const link = node.querySelector('[data-zeta-link]');
      const image = node.querySelector('[data-zeta-image]');
      const brand = node.querySelector('[data-zeta-brand]');
      const titleLink = node.querySelector('[data-zeta-title-link]');
      const price = node.querySelector('[data-zeta-price]');
      const compare = node.querySelector('[data-zeta-compare]');
      const color = node.querySelector('[data-zeta-color]');
      const labels = node.querySelector('[data-zeta-labels]');
      const wishlistSlot = node.querySelector('[data-zeta-wishlist-slot]');

      if (wishlistSlot) {
        wishlistSlot.dataset.zetaItemId = item.id;
        wishlistSlot.dataset.zetaSku = item.sku;
        wishlistSlot.dataset.zetaColorCode = item.colorCode;
        wishlistSlot.dataset.zetaColorName = item.colorName;
        wishlistSlot.dataset.zetaSoldOut = item.soldOut ? '1' : '0';

        const soldOverlay = document.createElement('div');
        soldOverlay.className = 'c-product-card__sold';
        soldOverlay.dataset.zetaSold = '';
        soldOverlay.setAttribute('aria-hidden', 'true');
        soldOverlay.textContent = 'SOLD OUT';
        soldOverlay.hidden = !item.soldOut;
        wishlistSlot.appendChild(soldOverlay);
      }

      link.href = item.url;
      if (item.imageUrl) {
        image.src = item.imageUrl;
        image.alt = item.title;
      }

      brand.textContent = item.brand;
      titleLink.href = item.url;
      titleLink.textContent = item.title;

      const discountedPrice = window.Anatai?.getDiscountedPrice(item.price) ?? null;
      if (discountedPrice !== null) {
        price.textContent = window.Zeta.formatMoney(discountedPrice);
        price.classList.add('price--highlight');
        compare.textContent = window.Zeta.formatMoney(item.price);
        compare.hidden = false;
      } else {
        price.textContent = window.Zeta.formatMoney(item.price);
        price.classList.toggle('price--highlight', item.onSale);
        if (item.compareAtPrice != null) {
          compare.textContent = window.Zeta.formatMoney(item.compareAtPrice);
          compare.hidden = false;
        }
      }

      if (item.colorName) {
        color.textContent = item.colorName;
      } else {
        color.remove();
      }

      item.badges.filter((badge) => badge.key !== 'soldout').forEach((badge) => {
        const span = document.createElement('span');
        span.className = `label label--${badge.key}`;
        span.textContent = badge.label;
        labels.appendChild(span);
      });
      void window.Zeta.attachNewBadge(labels, item.id);

      return node;
    }

    renderFacets(facets) {
      if (!this.refs.facets) {
        return;
      }
      this.currentFacets = facets;
      this.refs.facets.innerHTML = '';
      this.refs.facets.appendChild(this.buildInstockToggle());

      FACET_DEFS.forEach((def) => {
        const list = facets[def.key];
        if (!Array.isArray(list) || list.length === 0) {
          return;
        }
        const filteredList = list
          .filter((option) => this.resolveOptionValue(def, option))
          .map((option) => ({
            ...option,
            child: Array.isArray(option.child)
              ? option.child.filter((child) => this.resolveOptionValue(def, child))
              : option.child,
          }));
        if (filteredList.length === 0 || (filteredList.length < 2 && !def.hasChild)) {
          return;
        }
        this.refs.facets.appendChild(this.buildFacetGroup(def, filteredList));
      });
      this.refs.facets.appendChild(this.buildPriceRange());
    }

    buildInstockToggle() {
      const wrapper = document.createElement('div');
      wrapper.className = 'zeta-facet zeta-facet--toggle';

      const label = document.createElement('label');
      label.className = 'zeta-facet__option';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'zeta-facet__checkbox';
      input.checked = this.draft.instock;
      input.addEventListener('change', () => {
        this.draft.instock = input.checked;
        this.renderActiveFilters();
      });

      const text = document.createElement('span');
      text.className = 'zeta-facet__option-name';
      text.textContent = '在庫ありのみ';

      label.appendChild(input);
      label.appendChild(text);
      wrapper.appendChild(label);
      return wrapper;
    }

    buildFacetGroup(def, list) {
      const node = this.refs.facetGroupTemplate.content.cloneNode(true);
      node.querySelector('.zeta-facet').classList.add(`zeta-facet--${def.key}`);
      const heading = node.querySelector('[data-zeta-facet-heading]');
      const label = node.querySelector('[data-zeta-facet-label]');
      const body = node.querySelector('[data-zeta-facet-body]');

      label.textContent = def.label;
      body.classList.toggle('zeta-facet__body--swatches', def.key === 'color');
      heading.setAttribute('aria-expanded', 'true');
      body.hidden = false;
      heading.addEventListener('click', () => {
        const expanded = heading.getAttribute('aria-expanded') === 'true';
        heading.setAttribute('aria-expanded', String(!expanded));
        body.hidden = expanded;
      });

      list.forEach((option) => {
        body.appendChild(this.buildFacetOption(def, option));
        if (def.hasChild && Array.isArray(option.child)) {
          option.child.forEach((child) => {
            body.appendChild(this.buildFacetOption(def, child, true));
          });
        }
      });

      return node;
    }

    buildFacetOption(def, option, isChild = false) {
      const node = this.refs.facetOptionTemplate.content.cloneNode(true);
      const wrapper = node.querySelector('.zeta-facet__option');
      const input = node.querySelector('[data-zeta-facet-input]');
      const name = node.querySelector('[data-zeta-facet-name]');
      const count = node.querySelector('[data-zeta-facet-count]');

      const value = this.resolveOptionValue(def, option);
      const displayName = option.name || option.value;
      this.optionLabels[`${def.key}:${value}`] = displayName;

      if (isChild) {
        wrapper.classList.add('zeta-facet__option--child');
      }
      if (def.key === 'color') {
        return this.buildColorSwatch(value, displayName);
      }
      input.checked = this.draft.filters[def.key].includes(value);
      input.value = value;
      name.textContent = def.key === 'price' ? window.Zeta.formatPriceFacet(displayName) : displayName;
      count.textContent = `(${option.count})`;
      input.addEventListener('change', () => {
        this.toggleFilter(def.key, value, input.checked);
      });

      return node;
    }

    buildColorSwatch(value, displayName) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zeta-facet__swatch';
      button.setAttribute('aria-label', displayName);
      button.setAttribute('title', displayName);
      button.setAttribute('aria-pressed', String(this.draft.filters.color.includes(value)));
      button.style.setProperty('--zeta-swatch-color', window.Zeta.colorSwatchMap[displayName] || '#ffffff');
      button.addEventListener('click', () => {
        this.toggleFilter('color', value, !this.draft.filters.color.includes(value));
        button.setAttribute('aria-pressed', String(this.draft.filters.color.includes(value)));
      });
      return button;
    }

    buildPriceRange() {
      const wrapper = document.createElement('div');
      wrapper.className = 'zeta-facet zeta-facet--price';

      const title = document.createElement('p');
      title.className = 'zeta-facet__heading zeta-facet__heading--static';
      title.textContent = '価格';

      const controls = document.createElement('div');
      controls.className = 'zeta-price-range';
      const inputs = document.createElement('div');
      inputs.className = 'zeta-price-range__inputs';
      const minimumInput = this.buildPriceNumberInput('￥下限なし');
      const maximumInput = this.buildPriceNumberInput('￥上限なし');
      inputs.appendChild(minimumInput);
      inputs.appendChild(maximumInput);

      const slider = document.createElement('div');
      slider.className = 'zeta-price-range__slider';
      const minimumRange = this.buildPriceRangeInput('下限価格');
      const maximumRange = this.buildPriceRangeInput('上限価格');
      const labels = document.createElement('div');
      labels.className = 'zeta-price-range__labels';
      const minimumLabel = document.createElement('span');
      const maximumLabel = document.createElement('span');
      labels.appendChild(minimumLabel);
      labels.appendChild(maximumLabel);
      const parsed = this.parsePriceRange(this.draft.filters.price[0]);
      minimumInput.value = parsed.minimum == null ? '' : String(parsed.minimum);
      maximumInput.value = parsed.maximum == null ? '' : String(parsed.maximum);
      minimumRange.value = String(parsed.minimum == null ? PRICE_SLIDER_MIN : parsed.minimum);
      maximumRange.value = String(parsed.maximum == null ? this.priceSliderMaximum : parsed.maximum);
      this.updatePriceSlider(slider, minimumRange, maximumRange, minimumLabel, maximumLabel);

      const syncFromNumbers = (changedInput) => {
        let minimum = minimumInput.value === '' ? null : Number(minimumInput.value);
        let maximum = maximumInput.value === '' ? null : Number(maximumInput.value);
        minimum = minimum == null ? null : Math.max(PRICE_SLIDER_MIN, Math.min(this.priceSliderMaximum, minimum));
        maximum = maximum == null ? null : Math.max(PRICE_SLIDER_MIN, Math.min(this.priceSliderMaximum, maximum));
        if (minimum != null && maximum != null && minimum > maximum) {
          if (changedInput === minimumInput) maximum = minimum;
          else minimum = maximum;
        }
        minimumInput.value = minimum == null ? '' : String(minimum);
        maximumInput.value = maximum == null ? '' : String(maximum);
        minimumRange.value = String(minimum == null ? PRICE_SLIDER_MIN : minimum);
        maximumRange.value = String(maximum == null ? this.priceSliderMaximum : maximum);
        this.setDraftPrice(minimum, maximum);
        this.updatePriceSlider(slider, minimumRange, maximumRange, minimumLabel, maximumLabel);
      };
      const syncFromRanges = () => {
        let minimum = Number(minimumRange.value);
        let maximum = Number(maximumRange.value);
        if (minimum > maximum) {
          if (document.activeElement === minimumRange) maximum = minimum;
          else minimum = maximum;
        }
        minimumRange.value = String(minimum);
        maximumRange.value = String(maximum);
        minimumInput.value = minimum === PRICE_SLIDER_MIN ? '' : String(minimum);
        maximumInput.value = maximum === this.priceSliderMaximum ? '' : String(maximum);
        this.setDraftPrice(minimum === PRICE_SLIDER_MIN ? null : minimum, maximum === this.priceSliderMaximum ? null : maximum);
        this.updatePriceSlider(slider, minimumRange, maximumRange, minimumLabel, maximumLabel);
      };
      minimumInput.addEventListener('change', () => syncFromNumbers(minimumInput));
      maximumInput.addEventListener('change', () => syncFromNumbers(maximumInput));
      minimumRange.addEventListener('input', syncFromRanges);
      maximumRange.addEventListener('input', syncFromRanges);
      slider.appendChild(minimumRange);
      slider.appendChild(maximumRange);

      controls.appendChild(inputs);
      controls.appendChild(slider);
      controls.appendChild(labels);
      wrapper.appendChild(title);
      wrapper.appendChild(controls);
      return wrapper;
    }

    buildPriceNumberInput(placeholder) {
      const input = document.createElement('input');
      input.type = 'number';
      input.className = 'zeta-price-range__number';
      input.min = String(PRICE_SLIDER_MIN);
      input.max = String(this.priceSliderMaximum);
      input.step = String(PRICE_SLIDER_STEP);
      input.placeholder = placeholder;
      return input;
    }

    buildPriceRangeInput(label) {
      const input = document.createElement('input');
      input.type = 'range';
      input.className = 'zeta-price-range__range';
      input.min = String(PRICE_SLIDER_MIN);
      input.max = String(this.priceSliderMaximum);
      input.step = String(PRICE_SLIDER_STEP);
      input.setAttribute('aria-label', label);
      return input;
    }

    parsePriceRange(value) {
      if (!value) return { minimum: null, maximum: null };
      const [minimumValue, maximumValue] = String(value).split('-');
      return {
        minimum: minimumValue && Number(minimumValue) > PRICE_SLIDER_MIN ? Number(minimumValue) : null,
        maximum: maximumValue ? Number(maximumValue) : null,
      };
    }

    setDraftPrice(minimum, maximum) {
      const normalizedMinimum = minimum == null ? null : Math.max(PRICE_SLIDER_MIN, Math.min(this.priceSliderMaximum, minimum));
      const normalizedMaximum = maximum == null ? null : Math.max(PRICE_SLIDER_MIN, Math.min(this.priceSliderMaximum, maximum));
      if (normalizedMinimum != null && normalizedMaximum != null && normalizedMinimum > normalizedMaximum) return;
      this.draft.filters.price = normalizedMinimum == null && normalizedMaximum == null
        ? []
        : [`${normalizedMinimum == null ? PRICE_SLIDER_MIN : normalizedMinimum}-${normalizedMaximum == null ? '' : normalizedMaximum}`];
      this.renderActiveFilters();
    }

    updatePriceSlider(slider, minimumRange, maximumRange, minimumLabel, maximumLabel) {
      const span = this.priceSliderMaximum - PRICE_SLIDER_MIN;
      slider.style.setProperty('--zeta-price-min', `${((Number(minimumRange.value) - PRICE_SLIDER_MIN) / span) * 100}%`);
      slider.style.setProperty('--zeta-price-max', `${((Number(maximumRange.value) - PRICE_SLIDER_MIN) / span) * 100}%`);
      minimumLabel.textContent = window.Zeta.formatMoney(Number(minimumRange.value));
      maximumLabel.textContent = window.Zeta.formatMoney(Number(maximumRange.value));
    }

    resolveOptionValue(def, option) {
      const rawValue = def.useName ? option.name : option.value;
      if (rawValue == null || String(rawValue).trim() === '') {
        return '';
      }
      return String(rawValue);
    }

    toggleFilter(key, value, checked) {
      const current = this.draft.filters[key];
      if (checked && !current.includes(value)) {
        current.push(value);
      } else if (!checked) {
        this.draft.filters[key] = current.filter((v) => v !== value);
      }
      this.renderActiveFilters();
    }

    renderActiveFilters() {
      if (!this.refs.active) {
        return;
      }
      this.refs.active.innerHTML = '';

      const chips = [];
      FILTER_KEYS.forEach((key) => {
        this.draft.filters[key].forEach((value) => {
          chips.push({ key, value });
        });
      });
      if (this.draft.instock) {
        chips.push({ key: 'instock', value: '在庫ありのみ' });
      }

      if (chips.length === 0) {
        this.refs.active.hidden = true;
        return;
      }
      this.refs.active.hidden = false;

      chips.forEach((chip) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'zeta-search__chip';

        const labelSpan = document.createElement('span');
        labelSpan.textContent = this.formatChipLabel(chip);
        const closeSpan = document.createElement('span');
        closeSpan.setAttribute('aria-hidden', 'true');
        closeSpan.textContent = '×';
        button.appendChild(labelSpan);
        button.appendChild(closeSpan);

        button.addEventListener('click', () => {
          if (chip.key === 'instock') {
            this.draft.instock = false;
          } else {
            this.draft.filters[chip.key] = this.draft.filters[chip.key].filter((v) => v !== chip.value);
          }
          this.renderFacets(this.currentFacets || {});
          this.renderActiveFilters();
        });

        this.refs.active.appendChild(button);
      });

    }

    formatChipLabel(chip) {
      if (chip.key === 'category') return this.optionLabels[`category:${chip.value}`] || chip.value;
      if (chip.key === 'price') return window.Zeta.formatPriceFacet(chip.value);
      return chip.value;
    }

    renderPagination(total) {
      const totalPages = Math.ceil(total / this.perPage);
      if (totalPages <= 1) {
        this.refs.pagination.hidden = true;
        return;
      }
      this.refs.pagination.hidden = false;
      this.refs.pagination.innerHTML = '';

      const prev = this.buildPagerButton('‹', this.state.page > 1, () => {
        this.goToPage(this.state.page - 1);
      });
      const next = this.buildPagerButton('›', this.state.page < totalPages, () => {
        this.goToPage(this.state.page + 1);
      });
      prev.classList.add('zeta-search__pagination-button--arrow');
      next.classList.add('zeta-search__pagination-button--arrow');

      this.refs.pagination.appendChild(prev);
      this.getPaginationItems(totalPages).forEach((page) => {
        if (page === 'ellipsis') {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'zeta-search__pagination-ellipsis';
          ellipsis.textContent = '…';
          this.refs.pagination.appendChild(ellipsis);
          return;
        }
        const button = this.buildPagerButton(String(page), true, () => this.goToPage(page));
        button.classList.toggle('is-current', page === this.state.page);
        button.setAttribute('aria-current', page === this.state.page ? 'page' : 'false');
        this.refs.pagination.appendChild(button);
      });
      this.refs.pagination.appendChild(next);
    }

    getPaginationItems(totalPages) {
      const pages = [1, this.state.page - 1, this.state.page, this.state.page + 1, totalPages]
        .filter((page) => page >= 1 && page <= totalPages)
        .filter((page, index, values) => values.indexOf(page) === index)
        .sort((a, b) => a - b);
      const items = [];
      pages.forEach((page, index) => {
        if (index > 0 && page - pages[index - 1] > 1) items.push('ellipsis');
        items.push(page);
      });
      return items;
    }

    buildPagerButton(label, enabled, onClick) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zeta-search__pagination-button';
      button.textContent = label;
      button.disabled = !enabled;
      if (enabled) {
        button.addEventListener('click', onClick);
      }
      return button;
    }

    goToPage(page) {
      this.state.page = page;
      this.writeStateToUrl();
      this.search();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  if (!window.customElements.get('zeta-search-page')) {
    window.customElements.define('zeta-search-page', ZetaSearchPage);
  }
})();
